import React, { useEffect, useRef } from "react";
import { CloudUploadOutline, CopyOutline } from "@raresail/react-ionicons";
import { App } from "antd";
import { COMPONENT, FORMDATA, SELECT, TEXT } from "../../constants/constants";
import { API_DEPOSIT_ACCOUNTS, API_MANUAL_REPAYMENTS_SUBMIT } from "../../constants/apis";
import useGet from "../common/useGet";
import { TSet } from "../common/useSet";
import { showConfetti } from "../../components/elements/ConfettiView";
import { reloadData } from "../../components/lists/ListHandler";
import { InfoBox } from "../../components/elements/Elements";
import { PropOptions } from "../../utils/formUtils";
import { PRIMARY_COLOR, primary10, PRIMARY_LIGHT } from "../../constants/colors";
import { currencyFormat, stringify } from "../../utils/helpers";

/* ── Copy helper ─────────────────────────────────────────────── */

function CopyField(props: { label: string; value: string; onCopy: (v: string) => void }) {
  return (
    <div className="flexSpaceCenter font12" style={{ padding: "4px 0" }}>
      <div>
        <span className="grey">{props.label + ": "}</span>
        <span className="textDark semiBold">{props.value}</span>
      </div>
      <div className="main pointer font11 flexNullCenter gap3" onClick={() => props.onCopy(props.value)}>
        <CopyOutline fontSize={12} />
        <span>Copy</span>
      </div>
    </div>
  );
}

/* ── POP upload ──────────────────────────────────────────────── */

function POPUpload(props: { value?: File | null; onChange: (f: File | null) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const has = !!props.value;
  return (
    <div
      className="flexNullCenter gap12 r12 pointer animate"
      style={{
        padding: 14,
        border: "1.5px solid " + (has ? PRIMARY_COLOR : "#e8edf5"),
        background: has ? "rgba(0,51,153,0.03)" : "#fafbfc",
      }}
      onClick={() => { if (!has && ref.current) ref.current.click(); }}
    >
      <input
        ref={ref}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        hidden
        onChange={(e) => {
          const f = e.target.files ? e.target.files[0] : null;
          props.onChange(f || null);
          e.target.value = "";
        }}
      />
      <div
        style={{
          width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: has ? "#fff" : PRIMARY_COLOR,
          background: has ? PRIMARY_COLOR : primary10,
        }}
      >
        <CloudUploadOutline fontSize={20} />
      </div>
      <div className="flexGrow">
        <div className="font13 semiBold textDark">Proof of Payment</div>
        {has ? (
          <div className="font11 main semiBold">{"✓ " + (props.value as File).name}</div>
        ) : (
          <div className="font11 grey">Upload receipt (PDF, JPG, PNG)</div>
        )}
      </div>
      {has && (
        <div
          className="font12 red semiBold pointer"
          onClick={(e) => { e.stopPropagation(); props.onChange(null); }}
        >
          Remove
        </div>
      )}
    </div>
  );
}

/* ── Hook ─────────────────────────────────────────────────────── */

export const useManualRepaymentForm = ({ form, updateFormValues, state }: any) => {
  const loan = state || {};
  const loanId = loan.id;
  const loanCode = loan.loan_id;
  const remaining = loan.remaining_balance;
  const purpose = loan.purpose;
  const { message } = App.useApp();

  const { loadData, data: accountsRaw, loading: loadingAccounts } = useGet();

  useEffect(() => {
    loadData({ api: API_DEPOSIT_ACCOUNTS });
  }, []);

  // Handle both plain array and paginated { results: [] } response
  const accounts: any[] = Array.isArray(accountsRaw)
    ? accountsRaw
    : accountsRaw?.results || [];

  const selectedAccount = accounts.find((a: any) => a.id === form?.company_account_id);

  const copyText = (text: string) => {
    navigator?.clipboard?.writeText(text);
    message.info("Copied!");
  };

  const accountOptions = accounts.map((a: any) => {
    const typeLabel = a.account_type === "bank"
      ? a.bank_name
      : (a.momo_provider || "").replace(/_/g, " ");
    return { id: a.id, name: a.account_name + " (" + typeLabel + ")" };
  });

  /* Build selected account details component */
  let accountDetailsComponent: React.ReactNode = null;
  if (selectedAccount) {
    const isBank = selectedAccount.account_type === "bank";
    accountDetailsComponent = (
      <div className="lightBg r10 p12 flexColumn gap5 font12" style={{ border: "1px solid " + PRIMARY_LIGHT }}>
        {isBank ? (
          <React.Fragment>
            <CopyField label="Account" value={selectedAccount.account_number || ""} onCopy={copyText} />
            <CopyField label="Name" value={selectedAccount.account_name || ""} onCopy={copyText} />
          </React.Fragment>
        ) : (
          <CopyField label="Number" value={selectedAccount.phone_number || ""} onCopy={copyText} />
        )}
      </div>
    );
  }

  const fields: any[] = [
    {
      type: COMPONENT,
      component: (
        <div className="lightBg r12 p14 flexColumn gap8" style={{ border: "1px solid #e8edf5" }}>
          <div className="font14 semiBold textDark">Loan Summary</div>
          <div className="flexSpaceCenter font12">
            <span className="grey">Loan ID</span>
            <span className="semiBold textDark">{loanCode || "—"}</span>
          </div>
          <div className="flexSpaceCenter font12">
            <span className="grey">Purpose</span>
            <span className="semiBold textDark capitalize">{stringify(purpose)}</span>
          </div>
          <div className="flexSpaceCenter font12">
            <span className="grey">Remaining Balance</span>
            <span className="semiBold textDark">{currencyFormat(remaining)}</span>
          </div>
        </div>
      ),
    },
    {
      field: "channel",
      label: "How did you pay?",
      type: SELECT,
      required: true,
      options: [
        { id: "bank_transfer", name: "Bank Transfer" },
        { id: "mobile_money", name: "Mobile Money" },
      ],
    },
    {
      field: "company_account_id",
      label: "Paid To (Company Account)",
      type: SELECT,
      required: true,
      options: accountOptions,
      props: { loading: loadingAccounts },
    },
    {
      type: COMPONENT,
      visible: !!selectedAccount,
      component: accountDetailsComponent,
    },
    {
      label: "Amount Paid",
      field: "amount",
      type: TEXT,
      required: true,
      props: {
        ...PropOptions.number,
        prefix: "UGX",
        helperText: "Outstanding: " + currencyFormat(remaining),
      },
    },
    {
      field: "external_reference",
      label: "Transaction Reference",
      type: TEXT,
      props: { placeholder: "e.g. Bank ref, MoMo transaction ID" },
    },
    {
      field: "depositor_phone",
      label: "Phone Number Used",
      type: TEXT,
      visible: form?.channel === "mobile_money",
      props: { placeholder: "+256700123456" },
    },
    {
      type: COMPONENT,
      component: (
        <POPUpload
          value={form?.proof_of_payment}
          onChange={(file: File | null) => updateFormValues({ proof_of_payment: file })}
        />
      ),
    },
    {
      type: COMPONENT,
      component: (
        <InfoBox color="#f59e0b">
          Submit your proof of payment. Our team will verify it and apply
          the repayment to your loan. This usually takes 1–4 business hours.
        </InfoBox>
      ),
    },
  ];

  const onSubmit = ({ payload }: any): TSet => {
    const fd = new FormData();
    fd.append("loan_id", loanCode || loanId);
    fd.append("amount", payload.amount);
    fd.append("channel", payload.channel);
    if (payload.company_account_id) fd.append("company_account_id", payload.company_account_id);
    if (payload.external_reference) fd.append("external_reference", payload.external_reference);
    if (payload.depositor_phone) fd.append("depositor_phone", payload.depositor_phone);
    if (payload.proof_of_payment) fd.append("proof_of_payment", payload.proof_of_payment);
    return {
      api: API_MANUAL_REPAYMENTS_SUBMIT,
      params: fd,
      type: FORMDATA as any,
      successMessage: { message: "Repayment proof submitted! Awaiting verification.", duration: 7 },
      onSuccess: () => {
        reloadData("manual_repayments");
        reloadData("loans");
        showConfetti();
      },
    };
  };

  return {
    fields,
    onSubmit,
    title: "Submit Repayment Proof",
    submitText: "Submit",
    allowSubmit: !!form?.amount && parseFloat(form?.amount) > 0 && !!form?.channel,
  };
};